// src/store/slices/templateSlice.ts - SIMPLIFIED VERSION
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import logger from '../../utils/logger';

// ============= SIMPLE TYPES =============

export interface Template {
  _id: string;
  name: string;
  category: string;
  status: string;
  language: string;
  components: Array<{
    type: string;
    text?: string;
    format?: string;
    example?: any;
    buttons?: Array<{
      type: string;
      text: string;
      url?: string;
      phone_number?: string;
    }>;
  }>;
  metrics?: {
    attempted: number;
    sent: number;
    delivered: number;
    read: number;
    failed: number;
    replied: number;
  };
  filePaths?: string[];
  createdAt?: string;
  cachedAt?: string; // When we cached this template
}

export interface TemplateState {
  // Simple template cache - indexed by template ID
  templates: Record<string, Template>;
  
  // Total count for stats
  totalCached: number;
}

// ============= INITIAL STATE =============

const initialState: TemplateState = {
  templates: {},
  totalCached: 0,
};

// ============= SIMPLE SLICE =============

const templateSlice = createSlice({
  name: 'templates',
  initialState,
  reducers: {
    /**
     * 💾 Add a template to cache
     */
    addTemplate: (state, action: PayloadAction<Template>) => {
      const template = {
        ...action.payload,
        cachedAt: new Date().toISOString(),
      };
      
      state.templates[template._id] = template;
      state.totalCached = Object.keys(state.templates).length;
      
      logger.log('💾 Template Slice: Template added to cache:', template.name);
    },

    /**
     * 🗑️ Remove a template from cache
     */
    removeTemplate: (state, action: PayloadAction<string>) => {
      const templateId = action.payload;
      delete state.templates[templateId];
      state.totalCached = Object.keys(state.templates).length;
      
      logger.log('🗑️ Template Slice: Template removed from cache:', templateId);
    },

    /**
     * 🧹 Clear all cached templates
     */
    clearAllTemplates: (state) => {
      state.templates = {};
      state.totalCached = 0;
      
      logger.log('🧹 Template Slice: All templates cleared from cache');
    },

    /**
     * 🔄 Update template data
     */
    updateTemplate: (state, action: PayloadAction<{ templateId: string; updates: Partial<Template> }>) => {
      const { templateId, updates } = action.payload;
      
      if (state.templates[templateId]) {
        state.templates[templateId] = {
          ...state.templates[templateId],
          ...updates,
          cachedAt: new Date().toISOString(), // Update cache time
        };
        
        logger.log('🔄 Template Slice: Template updated:', templateId);
      }
    },
  },
});

// ============= ACTIONS EXPORT =============

export const {
  addTemplate,
  removeTemplate,
  clearAllTemplates,
  updateTemplate,
} = templateSlice.actions;

// ============= SIMPLE SELECTORS =============

/**
 * 🎯 Get a template by ID
 */
export const selectTemplateById = (templateId: string) => (state: RootState): Template | undefined => {
  return state.whatsapp.templates.templates[templateId];
};

/**
 * 🔍 Check if template exists in cache
 */
export const selectHasTemplate = (templateId: string) => (state: RootState): boolean => {
  return !!state.whatsapp.templates.templates[templateId];
};

/**
 * 📊 Get all cached templates
 */
export const selectAllTemplates = (state: RootState): Template[] => {
  return Object.values(state.whatsapp.templates.templates);
};

/**
 * 📈 Get cache stats
 */
export const selectCacheStats = (state: RootState) => {
  return {
    totalCached: state.whatsapp.templates.totalCached,
    templateIds: Object.keys(state.whatsapp.templates.templates),
  };
};

// ============= EXPORT REDUCER =============

export default templateSlice.reducer;